import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  Flex,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useDropzone, Accept } from 'react-dropzone';
import { ref, getDownloadURL } from 'firebase/storage';
import { CollabDataContext } from 'contexts/collabDataContext';
import { useLocation } from 'react-router-dom';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { deliverableApi } from '../../../services/api/deliverable';
import { getInfluencerPrimaryHandle } from '../../../utils/influencerUtils';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getClientIdFromCollabRequest } from 'utils/collabUtils';
import { uploadWithProgress, isVideoFile } from '../../../utils/contentUploadUtils';
import { isVideoUrl } from '../../../utils/fileUtils';
import { storage } from '../../../firebase';
import BottomSheet from './BottomSheet';
import BrandFeedbackBody from './BrandFeedbackBody';
import { SheetButton } from './counter-flow/sheetParts';
import {
  SubmissionSheetTitle,
  FeedbackBox,
  SheetLabel,
  StatusPill,
  DropzoneFace,
  dropzoneBoxProps,
  acceptedFormatsLabel,
  UploadProgressRow,
} from './submissionSheetParts';
import { ff } from '../../../theme';

interface DraftUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliverableKey: string;
  linkIndex: number;
  showFeedback: boolean;
  rejectionReason: string | null;
  /** Dedicated PDF from brand review (UGC `editRequestPdf`, deliverable `feedbackPDF`). */
  feedbackPdfUrl?: string | null;
  modalHeader: string;
  clientName: string;
  campaign: any;
  /**
   * When provided, the modal uploads through this handler instead of the
   * built-in draft submission — lets other flows (e.g. UGC content) reuse the
   * exact same bottom-sheet UI. Receives the file and a progress setter.
   */
  onSubmitFile?: (file: File, onProgress: (pct: number) => void) => Promise<void>;
  /** Overrides the accepted file types (defaults to video + image). */
  accept?: Accept;
  /** Overrides the submit button label. */
  submitLabel?: string;
  /** Overrides the dropzone placeholder text. */
  uploadLabel?: string;
  /** Previously submitted asset (storage path or URL), shown when resubmitting after rejection. */
  existingContentUrl?: string;
}

const DEFAULT_ACCEPT: Accept = {
  'video/*': ['.mp4', '.mov'],
  'image/*': ['.jpeg', '.jpg', '.png', '.heic', '.heif'],
};

const DraftUploadModal: React.FC<DraftUploadModalProps> = ({
  isOpen,
  onClose,
  deliverableKey,
  linkIndex,
  showFeedback,
  rejectionReason,
  feedbackPdfUrl,
  modalHeader,
  clientName,
  campaign,
  onSubmitFile,
  accept,
  submitLabel,
  uploadLabel,
  existingContentUrl,
}) => {
  const toast = useToast();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');
  const collabData = useContext(CollabDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const { influencerData } = useContext(InfluencerDataContext);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [resolvedExistingUrl, setResolvedExistingUrl] = useState<string | null>(null);

  const filePreviewUrl = useMemo(
    () => (uploadedFile ? URL.createObjectURL(uploadedFile) : null),
    [uploadedFile],
  );

  useEffect(() => () => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
  }, [filePreviewUrl]);

  useEffect(() => {
    if (!isOpen || !existingContentUrl) {
      setResolvedExistingUrl(null);
      return;
    }

    let cancelled = false;
    if (/^https?:\/\//i.test(existingContentUrl)) {
      setResolvedExistingUrl(existingContentUrl);
      return;
    }

    getDownloadURL(ref(storage, existingContentUrl))
      .then((url) => {
        if (!cancelled) setResolvedExistingUrl(url);
      })
      .catch(() => {
        if (!cancelled) setResolvedExistingUrl(null);
      });

    return () => { cancelled = true; };
  }, [isOpen, existingContentUrl]);

  const showExistingPreview = showFeedback && Boolean(existingContentUrl);
  const existingIsVideo = isVideoUrl(existingContentUrl ?? '');
  const replacementIsVideo = uploadedFile ? isVideoFile(uploadedFile) : existingIsVideo;

  const clients = useContext(ClientDataContext);
  const client = clients[getClientIdFromCollabRequest(collab, campaign)];

  const onDrop = (acceptedFiles: File[]) => {
    setUploadedFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: accept ?? DEFAULT_ACCEPT,
    maxFiles: 1,
    multiple: false,
    useFsAccessApi: false,
  });

  const handleClose = () => {
    setUploadedFile(null);
    setIsSubmitting(false);
    setUploadProgress(0);
    onClose();
  };

  const handleConfirm = async () => {
    if (!uploadedFile) {
      toast({ title: 'File is required.', description: 'Please upload a file before confirming.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    // Custom submission path (e.g. UGC content) — reuse the UI, swap the upload.
    if (onSubmitFile) {
      setIsSubmitting(true);
      setUploadProgress(0);
      try {
        await onSubmitFile(uploadedFile, setUploadProgress);
        toast({ title: 'Content submitted.', description: 'Your content has been submitted for review.', status: 'success', duration: 3000, isClosable: true });
        handleClose();
      } catch (error: any) {
        console.error('ERROR SUBMITTING CONTENT: ', error);
        let errorMessage = 'There was an error submitting your content.';
        if (error?.message) errorMessage = error.message;
        else if (error?.response?.status === 413) errorMessage = 'File is too large. Please try a smaller file.';
        else if (error?.name === 'AbortError') errorMessage = 'Upload was cancelled. Please try again.';
        else if (error?.message === 'Load failed') errorMessage = 'Upload failed. Please check your connection and try again.';
        toast({ title: 'Submission failed', description: errorMessage, status: 'error', duration: 5000, isClosable: true });
      } finally {
        setIsSubmitting(false);
        setUploadProgress(0);
      }
      return;
    }

    if (!collab) {
      toast({ title: 'Error', description: 'Collaboration data not found.', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    if (!client || !influencerData?.id || !deliverableKey || linkIndex === undefined) {
      toast({ title: 'Error', description: 'Missing required data for submission.', status: 'error', duration: 3000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      const { url, filePath } = await deliverableApi.getUploadUrl({
        fileName: uploadedFile.name,
        contentType: uploadedFile.type,
        collabId: collab.id,
        clientId: client.id,
        deliverableKey,
        linkIndex,
        submissionType: 'draft',
      }, influencerData?.id);

      await uploadWithProgress(url, uploadedFile, setUploadProgress);

      await deliverableApi.submitDraft({
        collabId: collab.id,
        clientId: client.id,
        clientEmail: client.email,
        influencerId: influencerData.id,
        influencerHandle: getInfluencerPrimaryHandle(influencerData),
        deliverableKey,
        linkIndex,
        filePath,
      }, influencerData?.id);

      toast({ title: 'Draft submitted.', description: 'Your draft has been submitted successfully.', status: 'success', duration: 3000, isClosable: true });
      handleClose();
    } catch (error: any) {
      console.error('ERROR SUBMITTING DRAFT: ', error);
      let errorMessage = 'There was an error submitting your draft.';
      if (error?.message) errorMessage = error.message;
      else if (error?.response?.status === 413) errorMessage = 'File is too large. Please try a smaller file.';
      else if (error?.name === 'AbortError') errorMessage = 'Upload was cancelled. Please try again.';
      else if (error?.message === 'Load failed') errorMessage = 'Upload failed. Please check your connection and try again.';

      toast({ title: 'Submission failed', description: errorMessage, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const buttonLabel = submitLabel ?? (showFeedback ? 'Submit revision' : 'Submit draft');

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      {/* Scrollable Content */}
      <Box overflowY="auto" px="20px" pt={1} pb="28px" fontFamily="display" color={ff.ink}>
        <SubmissionSheetTitle title={modalHeader} />

        <VStack spacing="16px" align="stretch">
          {/* Brand Feedback */}
          {showFeedback && (rejectionReason || feedbackPdfUrl) && (
            <FeedbackBox lead={`${clientName || 'The brand'} asked for edits:`}>
              <BrandFeedbackBody feedback={rejectionReason ?? ''} pdfUrl={feedbackPdfUrl} />
            </FeedbackBox>
          )}

          {showExistingPreview && (
            <Box>
              <Flex align="center" justify="space-between" mb="8px" gap={2}>
                <SheetLabel>Your submission</SheetLabel>
                <StatusPill tone="red">Needs edit</StatusPill>
              </Flex>
              <Box
                borderRadius="18px"
                overflow="hidden"
                bg={ff.ink}
                minH="140px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {!resolvedExistingUrl ? (
                  <Spinner size="md" color={ff.muted} thickness="2px" />
                ) : existingIsVideo ? (
                  <video
                    src={resolvedExistingUrl}
                    controls
                    playsInline
                    preload="metadata"
                    style={{ width: '100%', maxHeight: '280px', display: 'block', objectFit: 'contain' }}
                  />
                ) : (
                  <img
                    src={resolvedExistingUrl}
                    alt="Submitted content"
                    style={{ width: '100%', maxHeight: '280px', display: 'block', objectFit: 'contain' }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Upload File */}
          <Box>
            {showExistingPreview && <SheetLabel>Upload replacement</SheetLabel>}
            <Box
              {...getRootProps()}
              {...dropzoneBoxProps}
              {...(uploadedFile ? { bg: ff.ink, border: '1px solid #E4E4E0' } : {})}
            >
              <input {...getInputProps()} />
              {uploadedFile && filePreviewUrl ? (
                replacementIsVideo ? (
                  <video
                    src={filePreviewUrl}
                    controls
                    playsInline
                    preload="metadata"
                    style={{ width: '100%', maxHeight: '280px', display: 'block', objectFit: 'contain' }}
                  />
                ) : (
                  <img
                    src={filePreviewUrl}
                    alt="Replacement content"
                    style={{ width: '100%', maxHeight: '280px', display: 'block', objectFit: 'contain' }}
                  />
                )
              ) : (
                <DropzoneFace
                  label={uploadLabel ?? (showFeedback ? 'Upload revised file' : 'Upload file')}
                  formats={acceptedFormatsLabel(accept ?? DEFAULT_ACCEPT)}
                />
              )}
            </Box>
          </Box>

          {isSubmitting && uploadedFile && (
            <UploadProgressRow fileName={uploadedFile.name} percent={uploadProgress} />
          )}

          {/* Submit Button */}
          <SheetButton
            tone="lime"
            onClick={handleConfirm}
            isDisabled={!uploadedFile || isSubmitting}
            isLoading={isSubmitting}
            w="full"
            flex="none"
            fontSize="16px"
          >
            {buttonLabel}
          </SheetButton>
        </VStack>
      </Box>
    </BottomSheet>
  );
};

export default DraftUploadModal;
